<script setup lang="ts">
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import { useVeeForm } from '@/components/forms/useVeeForm';
import { api } from '@/api';
import { routenames } from '@/router/routenames';
import {
  CreateOrUpdateUnitDtoTypeEnum,
  type UnitDto,
  type CreateOrUpdateUnitDto,
  UnitDtoTypeEnum,
} from '@/api/generator';
import UnitForm from './components/UnitForm.vue';
import { useLoading } from '@/utils/useLoading';
import { onMounted } from 'vue';
import VeeSubmitButton from '@/components/forms/SubmitButton/VeeSubmitButton.vue';
import BasePageStatus from '@/components/pages/BasePage/BasePageStatus.vue';
import { useToken } from '@/utils/tokens/tokens';
import { useRouter } from 'vue-router';
import { entityNotFoundErrorHandler, formBackendErrorHandler } from '@/errorHandling';
import { CreateOrUpdateUnitDtoToJSON } from '@/api/generator/models/CreateOrUpdateUnitDto';
import PtwCancelButton from '@/components/buttons/PtwCancelButton.vue';
import { useUnitsStore } from '@/stores/unitsStore';
import { backendValidator } from '@/validation/validators/backendValidator';

export interface UnitEditViewProps {
  id: string;
}

const props = defineProps<UnitEditViewProps>();
const router = useRouter();
const unitsStore = useUnitsStore();

const { action: loadUnit, loading } = useLoading(async () => {
  const { result } = await api.unitsApi.getUnit({ id: props.id }, [
    entityNotFoundErrorHandler(router),
  ]);
  if (result) {
    resetForm({ values: result });
  }
}, true);

const token = useToken({
  id: props.id,
  $type:
    UnitDtoTypeEnum.PtwPtw100AppZeroApplicationSharedDataTypeManagementDtoUnitDtoPtwPtw100AppZeroApplicationShared,
});

onMounted(token.request);

token.on.initialized(loadUnit);

token.on.inUse(async () => {
  await router.push({ name: routenames.units.overview });
});

const { form } = useVeeForm<UnitDto>({
  schemaName: 'UnitDto',
  mode: 'edit',
  customValidateFunctions: [
    backendValidator((enumerationQuantityDto, errorHandlers) =>
      api.unitsApi.validateUnit(
        {
          createOrUpdateUnitDto: toCreateOrUpdateUnitDto(enumerationQuantityDto),
        },
        errorHandlers,
      ),
    ),
  ],
});

const { values, resetForm, handleSubmit } = form;

function toCreateOrUpdateUnitDto(unitDto: UnitDto): CreateOrUpdateUnitDto {
  return {
    ...CreateOrUpdateUnitDtoToJSON(unitDto as CreateOrUpdateUnitDto),
    $type:
      CreateOrUpdateUnitDtoTypeEnum.PtwPtw100AppZeroApplicationSharedDataTypeManagementDtoCreateOrUpdateUnitDtoPtwPtw100AppZeroApplicationShared,
  };
}

const submit = handleSubmit(
  async (unitDto) => {
    const { success } = await unitsStore.updateUnit(
      props.id,
      {
        ...toCreateOrUpdateUnitDto(unitDto),
        $type:
          CreateOrUpdateUnitDtoTypeEnum.PtwPtw100AppZeroApplicationSharedDataTypeManagementDtoCreateOrUpdateUnitDtoPtwPtw100AppZeroApplicationShared,
      },
      [formBackendErrorHandler(form)],
    );

    if (success) {
      await router.push({
        name: routenames.units.overview,
      });
    }
  },
  {
    withReferenceCheck: true,
  },
);
</script>

<template>
  <form @submit="submit">
    <BasePage :loading="loading">
      <template #title>
        <BasePageTitle :subtitle="$t('Unit_one')" :title="values.name" :titleId="values.iuid" />
      </template>

      <template #status>
        <BasePageStatus :entity="values" />
      </template>

      <template #actions>
        <PtwCancelButton :to="{ name: routenames.units.overview }" />
        <VeeSubmitButton class="ml-4" />
      </template>

      <UnitForm />
    </BasePage>
  </form>
</template>
